import { Link } from "react-router-dom";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { ArrowRight, Feather, Boxes, Activity } from "lucide-react";
import { useApi } from "../lib/api";
import { fmtUsd, fmtNum, timeAgo, shortAddr } from "../lib/format";
import { StatCard, SectionHead, DataBadge, Skel, ErrorState } from "../components/common";
import TokenTable from "../components/TokenTable";
import usePageMeta from "../lib/usePageMeta";

const gasVal = (g) => (g == null ? null : typeof g === "object" ? g.price : g);

export default function Overview() {
  usePageMeta("Overview", "Live Robinhood Chain network stats, top tokens, tokenized stocks and latest blocks & transactions.");
  const { data: ov } = useApi("/market/overview", { refreshInterval: 60000 });
  const { data: tokens } = useApi("/tokens?sort=volume");
  const { data: stocks } = useApi("/stocks");
  const { data: blocks } = useApi("/explorer/blocks", { refreshInterval: 15000 });
  const { data: txs } = useApi("/explorer/txs", { refreshInterval: 15000 });
  const s = ov?.stats || {};

  return (
    <div className="space-y-5">
      <div className="t-card p-5 md:p-7 relative overflow-hidden" data-testid="hero-section">
        <div className="absolute right-6 top-1/2 -translate-y-1/2 opacity-[0.06] hidden md:block"><Feather size={180} /></div>
        <h1 className="font-heading font-black text-2xl md:text-4xl leading-tight max-w-2xl">
          Robinhood Chain market intelligence, <span className="text-[#10B981]">in one place.</span>
        </h1>
        <p className="text-[#9CA3AF] text-sm md:text-base mt-2 max-w-2xl">
          Discover tokens, analyze wallets, monitor tokenized stocks, and explore every transaction on Robinhood Chain — powered entirely by live onchain data.
        </p>
        <div className="flex gap-2 mt-4">
          <Link to="/tokens" data-testid="explore-market-button" className="px-4 py-2 bg-[#059669] hover:bg-[#047857] text-white text-[13px] font-medium rounded-sm transition-colors inline-flex items-center gap-2">
            Explore the Market <ArrowRight size={14} />
          </Link>
          <Link to="/stocks" data-testid="view-stocks-button" className="px-4 py-2 border border-[#262626] hover:border-[#3F3F46] text-[13px] rounded-sm transition-colors">
            View Stock Tokens
          </Link>
        </div>
      </div>

      {ov && !ov.live && <ErrorState />}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="market-stats">
        <StatCard label="Total Transactions" value={fmtNum(parseInt(s.total_transactions || 0))} testId="stat-txs" />
        <StatCard label="Transactions Today" value={fmtNum(parseInt(s.transactions_today || 0))} testId="stat-txs-today" />
        <StatCard label="Total Blocks" value={fmtNum(parseInt(s.total_blocks || 0))} testId="stat-blocks" />
        <StatCard label="Total Addresses" value={fmtNum(parseInt(s.total_addresses || 0))} testId="stat-addresses" />
        <StatCard label="ETH Price" value={s.coin_price ? `$${parseFloat(s.coin_price).toFixed(2)}` : "—"} testId="stat-eth" />
        <StatCard label="Avg Block Time" value={s.average_block_time ? `${(s.average_block_time / 1000).toFixed(2)}s` : "—"} testId="stat-blocktime" />
        <StatCard label="Gas Price" value={gasVal(s.gas_prices?.average) != null ? `${gasVal(s.gas_prices.average)} gwei` : "—"} sub={s.gas_prices ? `slow ${gasVal(s.gas_prices.slow) ?? "—"} · fast ${gasVal(s.gas_prices.fast) ?? "—"}` : ""} testId="stat-gas" />
        <StatCard label="Top-50 Token Volume 24h" value={ov ? fmtUsd(ov.top50_volume_24h) : "—"} testId="stat-volume" />
      </div>

      <div className="t-card">
        <div className="flex items-center justify-between px-3 py-2 border-b border-[#262626]">
          <span className="font-heading font-bold text-sm uppercase tracking-wide">Network Transactions — Daily</span>
          <DataBadge live={ov?.live ?? true} />
        </div>
        <div className="h-56 p-2" data-testid="tx-activity-chart">
          {ov ? (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={ov.tx_chart}>
                <defs><linearGradient id="g1" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#059669" stopOpacity={0.25} /><stop offset="100%" stopColor="#059669" stopOpacity={0} /></linearGradient></defs>
                <XAxis dataKey="date" stroke="#3F3F46" fontSize={10} tickLine={false} />
                <YAxis tickFormatter={(v) => fmtNum(v)} stroke="#3F3F46" fontSize={10} tickLine={false} width={55} />
                <Tooltip contentStyle={{ background: "#121212", border: "1px solid #262626", borderRadius: 2, fontSize: 12 }} formatter={(v) => [fmtNum(v), "Transactions"]} />
                <Area type="monotone" dataKey="transactions_count" stroke="#10B981" strokeWidth={1.5} fill="url(#g1)" />
              </AreaChart>
            </ResponsiveContainer>
          ) : <Skel rows={4} h={44} />}
        </div>
      </div>

      <div>
        <SectionHead title="Top Tokens by Volume" badge={<DataBadge live={tokens?.live ?? true} />} right={<Link to="/tokens" className="text-xs text-[#10B981] hover:underline" data-testid="view-all-tokens">View all</Link>} />
        <div className="t-card"><TokenTable tokens={tokens?.items?.slice(0, 10)} loading={!tokens} testId="top-tokens-table" /></div>
      </div>

      <div>
        <SectionHead title="Tokenized Stocks" badge={<DataBadge live={stocks?.live ?? true} />} right={<Link to="/stocks" className="text-xs text-[#10B981] hover:underline" data-testid="view-all-stocks">View all</Link>} />
        <div className="t-card"><TokenTable tokens={stocks?.items?.slice(0, 8)} loading={!stocks} testId="stocks-overview-table" /></div>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <div>
          <SectionHead title="Latest Blocks" badge={<DataBadge live={blocks?.live ?? true} />} />
          <div className="t-card" data-testid="latest-blocks">
            {!blocks ? <Skel rows={8} h={34} /> : blocks.items.slice(0, 10).map((b) => (
              <Link key={b.height} to={`/explorer/block/${b.height}`} className="flex items-center gap-3 px-3 py-2 border-b border-[#1a1a1a] last:border-0 hover:bg-[#1A1A1A] transition-colors" data-testid={`block-row-${b.height}`}>
                <Boxes size={14} className="text-[#059669] shrink-0" />
                <span className="num text-[13px] text-[#10B981]">#{b.height}</span>
                <span className="text-xs text-[#6B7280]">{b.transactions_count ?? b.transaction_count ?? 0} txs</span>
                <span className="ml-auto num text-xs text-[#6B7280]">{b.timestamp ? timeAgo(new Date(b.timestamp).getTime() / 1000) : ""} ago</span>
              </Link>
            ))}
          </div>
        </div>
        <div>
          <SectionHead title="Latest Transactions" badge={<DataBadge live={txs?.live ?? true} />} />
          <div className="t-card" data-testid="latest-txs">
            {!txs ? <Skel rows={8} h={34} /> : txs.items.slice(0, 10).map((t) => (
              <Link key={t.hash} to={`/explorer/tx/${t.hash}`} className="flex items-center gap-3 px-3 py-2 border-b border-[#1a1a1a] last:border-0 hover:bg-[#1A1A1A] transition-colors" data-testid={`tx-row-${t.hash.slice(2, 8)}`}>
                <Activity size={14} className="text-[#059669] shrink-0" />
                <span className="num text-[13px] text-[#10B981]">{shortAddr(t.hash, 6)}</span>
                <span className="text-xs text-[#6B7280]">{t.method || "transfer"}</span>
                <span className="ml-auto num text-xs text-[#6B7280]">{t.timestamp ? timeAgo(new Date(t.timestamp).getTime() / 1000) : ""} ago</span>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
